import React from 'react';
import Divider from '@material-ui/core/Divider';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import Radio from '@material-ui/core/Radio';
import styled from 'styled-components';
import { PhoneNumberModel } from '@utmost.co/enterprise-models';
import { SystemIconButton } from '@utmost.co/utmost-design-system';
import { SelectValue, InternationalPhoneNumberAtom, InternationalPhoneNumberValue } from '../../atoms/index';
import { ComponentPropBase } from '../../types';
import { InternationalPhoneNumber, useFieldSmartFormContext } from '../../forms/utils';
import { Buttons } from '../../atoms/Button/Buttons';
import { PhoneNumberRowReadOnly } from './PhoneNumberRowReadOnly';

export interface PhoneNumberRowProps extends ComponentPropBase {
  onClickDelete: (args: number) => void;
  rowIndex: number;
  onClickPrimaryRadio: (args: number) => void;
  deviceTypes: SelectValue[];
  disabled?: boolean;
  readonly?: boolean;
}

export const PhoneNumberRow: React.FC<Partial<PhoneNumberModel> & PhoneNumberRowProps> = ({
  name,
  onClickPrimaryRadio,
  rowIndex,
  onClickDelete,
  deviceTypes,
  disabled,
  readonly
}) => {
  const fieldNameWithIndex = name;

  const { field, setFieldValue, setFieldTouched, getFieldPermissions, translations } =
    useFieldSmartFormContext<Partial<PhoneNumberModel>>(fieldNameWithIndex);
  const internationalValue: InternationalPhoneNumberValue = InternationalPhoneNumber.toInternationalPhoneNumberValue(
    field.value
  );
  const permissions = getFieldPermissions(fieldNameWithIndex);

  if (readonly) {
    return <PhoneNumberRowReadOnly name={name} value={field.value} deviceTypes={deviceTypes} />;
  }

  const deviceTypeName = `${fieldNameWithIndex}.deviceType`;

  return (
    <>
      <Grid
        data-test={`${fieldNameWithIndex}-phone-number-row`}
        container
        spacing={2}
        direction="row"
        alignContent="flex-start"
      >
        <Grid item container spacing={2} xs={11}>
          {!!deviceTypes.length && (
            <Grid item xs={12}>
              <Buttons
                options={deviceTypes}
                disabled={disabled}
                value={field.value?.deviceType}
                onChange={(value) => {
                  setFieldTouched(deviceTypeName, true, false);
                  setFieldValue(deviceTypeName, value?.value);
                }}
              />

              {/* WE MAY NEED THIS IN THE NEAR FUTURE (07.12.2021). If older than 3mo remove this */}
              {/*            <Select
              id={deviceTypeName}
              disabled={disabled}
              test-id={deviceTypeName}
              value={deviceTypes.length ? field.value?.deviceType : ""}
              onChange={(e) => {
                setFieldValue(deviceTypeName, e.target.value);
              }}
              onBlur={() => {
                setFieldTouched(deviceTypeName);
              }}
              name={deviceTypeName}
              multiple={false}
              fullWidth
            >
              <MenuItem key="" value="" disabled>
                Device Type
              </MenuItem>
              {deviceTypes.map((type) => {
                return (
                  <MenuItem
                    key={type.key}
                    test-id={fieldNameWithIndex}
                    value={type.value}
                    selected={type.value === field.value?.deviceType}
                  >
                    {type.label}
                  </MenuItem>
                );
              })}
            </Select> */}
            </Grid>
          )}
          <Grid item xs={12}>
            <InternationalPhoneNumberAtom
              key={name}
              dataTest={name}
              disabled={disabled}
              value={internationalValue}
              onChange={(value) => {
                field.setValue(InternationalPhoneNumber.mergeToPhoneNumberModel(value?.model, field.value));
              }}
              onBlur={() => {
                field.setTouched();
              }}
              onModelInit={(isValid) => {
                if (internationalValue?.model && !internationalValue?.model.countryCode && isValid) {
                  const newModel = new InternationalPhoneNumber(internationalValue?.model).getDetails().model;
                  field.setValue(InternationalPhoneNumber.mergeToPhoneNumberModel(newModel, field.value));
                }
              }}
              error={field.error}
              permission={permissions}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              value={field.value?.primary}
              aria-label={
                translations && translations.selectPhoneNumRowLabel
                  ? translations.selectPhoneNumRowLabel({ rowIndex })
                  : `select phone number row ${rowIndex} as primary`
              }
              style={{ marginTop: -10 }}
              control={
                <Radio
                  disabled={disabled}
                  onClick={() => {
                    onClickPrimaryRadio(rowIndex);
                  }}
                  checked={field.value?.primary}
                  color="primary"
                />
              }
              label={translations?.labelPrimaryLabel || 'Primary'}
            />
          </Grid>
        </Grid>
        {!disabled && (
          <Grid item xs={1}>
            <StyledErrorIconButton
              test-data={`${fieldNameWithIndex}-delete-row-button`}
              iconName="delete"
              onClick={() => onClickDelete(rowIndex)}
              style={{ marginTop: -7 }}
            />
          </Grid>
        )}
      </Grid>
      <StyledDivider />
    </>
  );
};

const StyledErrorIconButton = styled(SystemIconButton).attrs((props) => ({
  color: props.theme?.colorAlert
}))``;

const StyledDivider = styled(Divider)`
  && {
    margin: 16px 0;
  }
`;
